import { Link } from "react-router-dom";
import { Check, ArrowRight } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { useSEO } from "@/components/seo";
import { Reveal, SectionHead, Marquee } from "@/components/motion";

const TURN_IMG = "https://images.unsplash.com/photo-1666634157070-6fd830fb5672?crop=entropy&cs=srgb&fm=jpg&q=80";
const MILL_IMG = "https://images.unsplash.com/photo-1713371398485-7bde1bde9def?crop=entropy&cs=srgb&fm=jpg&q=80";
const PRINT_IMG = "https://images.unsplash.com/photo-1642969164999-979483e21601?crop=entropy&cs=srgb&fm=jpg&q=80";

function ServiceChapter({ num, title, points, note, img, imgAlt, flip }) {
  return (
    <div className={`grid items-center gap-10 lg:grid-cols-2 ${flip ? "" : ""}`} data-testid={`service-chapter-${num}`}>
      <Reveal className={flip ? "lg:order-2" : ""}>
        <div className="clip-frame overflow-hidden">
          <img src={img} alt={imgAlt} className="h-72 w-full object-cover transition-transform duration-700 hover:scale-105 lg:h-96" loading="lazy" />
        </div>
      </Reveal>
      <div className={flip ? "lg:order-1" : ""}>
        <Reveal>
          <span className="font-mono-tech text-5xl font-semibold text-white/10">{num}</span>
          <h2 className="mt-2 text-2xl font-black uppercase tracking-tight text-white sm:text-3xl">{title}</h2>
          <ul className="mt-6 space-y-3">
            {points.map((p) => (
              <li key={p} className="flex items-start gap-3 text-sm text-slate-300">
                <Check size={16} className="mt-0.5 shrink-0 text-royal-soft" />
                {p}
              </li>
            ))}
          </ul>
          {note && <p className="mt-6 border-l-2 border-royal pl-4 text-sm italic text-slate-400">{note}</p>}
        </Reveal>
      </div>
    </div>
  );
}

export default function Services() {
  const { t, pathFor } = useLang();
  useSEO("services");
  const s = t.servicesPage;

  return (
    <div className="bg-anthracite-dark pt-16" data-testid="services-page">
      <section className="border-b border-white/10 bg-anthracite grid-lines-dark">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-8 lg:py-28">
          <SectionHead kicker={s.kicker} title={s.title} light testid="services" />
          <Reveal delay={0.1}>
            <p className="mt-6 max-w-2xl text-slate-400">{s.intro}</p>
          </Reveal>
        </div>
      </section>

      <Marquee items={t.marquee} />

      <section className="mx-auto max-w-7xl space-y-24 px-4 py-20 sm:px-8 lg:py-28">
        <ServiceChapter num="01" title={s.turning.title} points={s.turning.points} img={TURN_IMG} imgAlt="CNC-Drehbearbeitung eines Metallteils" />
        <ServiceChapter num="02" title={s.milling.title} points={s.milling.points} note={s.milling.note} img={MILL_IMG} imgAlt="CNC-Fräsbearbeitung eines Bauteils" flip />
        <ServiceChapter num="03" title={s.printing.title} points={s.printing.points} img={PRINT_IMG} imgAlt="Industrieller 3D-Druck eines Bauteils" />
        <Reveal>
          <div className="border border-white/10 bg-anthracite p-8 lg:p-12" data-testid="service-extras">
            <span className="font-mono-tech text-5xl font-semibold text-white/10">04</span>
            <h2 className="mt-2 text-2xl font-black uppercase tracking-tight text-white sm:text-3xl">{s.extras.title}</h2>
            <div className="mt-8 grid gap-x-10 gap-y-3 sm:grid-cols-2">
              {s.extras.points.map((p) => (
                <div key={p} className="flex items-start gap-3 text-sm text-slate-300">
                  <Check size={16} className="mt-0.5 shrink-0 text-royal-soft" />
                  {p}
                </div>
              ))}
            </div>
          </div>
        </Reveal>
      </section>

      <section className="bg-slate-50 grid-lines-light" data-testid="materials-industries-section">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-8 lg:py-28">
          <div className="grid gap-16 lg:grid-cols-2">
            <div>
              <SectionHead kicker="01" title={s.materialsTitle} testid="materials" />
              <Reveal delay={0.08}>
                <div className="mt-8 flex flex-wrap gap-3">
                  {s.materials.map((m) => (
                    <span key={m} className="border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700">{m}</span>
                  ))}
                </div>
                <p className="mt-6 text-sm text-slate-500">{s.materialsNote}</p>
              </Reveal>
            </div>
            <div>
              <SectionHead kicker="02" title={s.industriesTitle} testid="industries" />
              <Reveal delay={0.08}>
                <div className="mt-8 grid grid-cols-2 gap-px border border-slate-200 bg-slate-200">
                  {s.industries.map((ind) => (
                    <div key={ind} className="bg-white px-4 py-4 text-sm font-medium text-slate-700 transition-colors hover:bg-anthracite hover:text-white">
                      {ind}
                    </div>
                  ))}
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      <section className="border-t border-white/10 bg-anthracite" data-testid="services-cta">
        <div className="mx-auto flex max-w-7xl flex-col items-start justify-between gap-8 px-4 py-16 sm:px-8 lg:flex-row lg:items-center">
          <div>
            <h2 className="text-2xl font-black uppercase tracking-tight text-white sm:text-3xl">{s.ctaTitle}</h2>
            <p className="mt-3 max-w-xl text-slate-400">{s.ctaText}</p>
          </div>
          <Link to={pathFor("inquiry")} className="group flex shrink-0 items-center gap-3 bg-royal px-8 py-4 text-sm font-bold uppercase tracking-wider text-white transition-colors hover:bg-royal-hover" data-testid="services-cta-button">
            {t.home.ctaButton}
            <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
          </Link>
        </div>
      </section>
    </div>
  );
}
